/**
 * PageNotes — fetches and renders the admin's decorative notes (post-its,
 * polaroids, letters, kraft tags, speech bubbles) for a given page or zone.
 *
 * Usage:
 *   <PageNotes page="home" />                      // renders the 4 corners + global
 *   <PageNotes page="home" zone="banner-top" />    // inline banner-row variant
 *   <PageNotes page="product_detail" productId="..." zone="after-hero" />
 *
 * The `page` value is one of: home, products, product_detail, producers,
 * producer_detail, global. The component automatically merges global notes
 * with page-specific ones server-side.
 */
import { useEffect, useState } from 'react';
import API from '@/lib/api';
import AdminNote from './AdminNote';

const CORNER_POSITIONS = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];
const INLINE_ZONES = ['banner-top', 'after-hero', 'footer-banner'];

const CORNER_CLASSES = {
  'top-left':     'fixed top-20 left-4 z-30 hidden md:block',
  'top-right':    'fixed top-20 right-4 z-30 hidden md:block',
  'bottom-left':  'fixed bottom-6 left-4 z-30 hidden md:block',
  'bottom-right': 'fixed bottom-6 right-4 z-30 hidden md:block',
};

const INLINE_WRAP_CLASSES = {
  'banner-top':    'flex flex-wrap items-start justify-center gap-4 py-6 px-4',
  'after-hero':    'flex flex-wrap items-start justify-center gap-6 py-8 px-4',
  'footer-banner': 'flex flex-wrap items-start justify-center gap-4 py-6 px-4',
};

export default function PageNotes({ page, productId = null, zone = 'corners' }) {
  const [notes, setNotes] = useState([]);

  useEffect(() => {
    if (!page) return;
    const params = { page };
    if (productId) params.product_id = productId;
    let cancelled = false;
    API.get('/admin-notes', { params })
      .then(r => { if (!cancelled) setNotes(r.data || []); })
      .catch(() => { if (!cancelled) setNotes([]); });
    return () => { cancelled = true; };
  }, [page, productId]);

  if (notes.length === 0) return null;

  if (zone === 'corners') {
    // For each of the 4 corners, render the FIRST matching note (avoid overlap)
    return (
      <>
        {CORNER_POSITIONS.map(pos => {
          const note = notes.find(n => n.position === pos);
          if (!note) return null;
          return (
            <div key={pos} className={CORNER_CLASSES[pos]} data-testid={`page-notes-${pos}`}>
              <AdminNote note={note} />
            </div>
          );
        })}
      </>
    );
  }

  if (INLINE_ZONES.includes(zone)) {
    const matching = notes.filter(n => n.position === zone);
    if (matching.length === 0) return null;
    return (
      <div className={INLINE_WRAP_CLASSES[zone]} data-testid={`page-notes-zone-${zone}`}>
        {matching.map(n => <AdminNote key={n.id} note={n} />)}
      </div>
    );
  }

  return null;
}
